import type { SelectOption } from '@aus-platform/design-system';
import {
  Button,
  Input,
  InputGroup,
  Spinner,
} from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { isEmpty, isNil, snakeCase } from 'lodash';
import { setVectorLayerVisibility } from 'map-3d/components';
import { MapTool } from 'map-3d/components/map-3d-container/map-3d-tools';
import { getEligibleLayerOptions } from 'map-3d/components/map-3d-sidebar/components/map-3d-workspace/shared/helpers';
import {
  selectActiveWorkspaceLayersByFeatureType,
  selectMap3DState,
  selectMap3dDataset,
  selectMap3dWorkspace,
  setCurrentActiveMapTool,
} from 'map-3d/shared/map-3d-slices';
import { useContext, useEffect, useMemo, useState } from 'react';
import { trackEvent } from 'shared/analytics';
import type { ContourPayloadType, FeatureResponseData } from 'shared/api';
import {
  FeatureCountLabel,
  FeatureGeometryType,
  FeatureType,
  handleResponseErrorMessage,
  handleResponseMessage,
  useAccessTagList,
  useFeatureList,
  useGenerateContour,
} from 'shared/api';
import { GlobalContext } from 'shared/context';
import { EventPropertyKey, Events, EventStatus } from 'shared/enums/events';
import { preventNonInteger, preventNonNumber } from 'shared/helpers';
import { useInputFields } from 'shared/hooks';
import { areAccessTagsRequiredForLayer } from 'src/sites/helpers';
import {
  FormErrorMessage,
  MarkArea,
  MarkAreaToggle,
  type WorkspaceLayer,
  type WorkspaceLayerListObj,
} from '../../../../shared';
import type {
  ContourFormType,
  ContourValidatorType,
  GenerateContourFormProps,
} from '../../types';
import { contourValidator } from './validators';

export const GenerateContourForm: React.FC<GenerateContourFormProps> = ({
  setShowContourForm,
  dsmElevationBounds,
}) => {
  // Contexts.
  const { loggedUser } = useContext(GlobalContext);

  // Dispatcher.
  const dispatch = useAppDispatch();

  // States.
  const [accessTagsList, setAccessTagsList] =
    useState<SelectOption<string>[]>();
  const [isDrawToolActive, setIsDrawToolActive] = useState<boolean>(false);
  const [currentMarkedArea, setCurrentMarkedArea] = useState<MarkArea>(
    MarkArea.Draw,
  );

  // Selectors.
  const { aoiManager } = useAppSelector(selectMap3DState);
  const { selectedProject, selectedTerrainSite, selectedTerrainIteration } =
    useAppSelector(selectMap3dDataset);
  const { layerAccessControl: accessControl, workspaceLayers } =
    useAppSelector(selectMap3dWorkspace);
  const activePolygonLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(
      state,
      FeatureGeometryType.Polygon,
    ),
  );

  // Drawing tools.
  const polygonTool = aoiManager?.polygonTool;

  const layerAccessTagsRequired = areAccessTagsRequiredForLayer(
    accessControl.accessType,
    loggedUser,
  );

  // Constants.
  const contourInputInitialState: ContourFormType = {
    name: '',
    accessTags: [],
    minorInterval: 5,
    majorInterval: 10,
    lowestAltitude: dsmElevationBounds?.min ?? 0,
    highestAltitude: dsmElevationBounds?.max ?? 0,
    thresholdValue: 5,
    smoothingFilterSize: 10,
    workspaceLayer: null,
  };

  const validatorInitialState: ContourValidatorType = {
    highestAltitude: {
      lowerBound: 0,
    },
    lowestAltitude: {
      upperBound: 0,
    },
    accessTags: {
      isRequired: layerAccessTagsRequired,
    },
  };

  // Hooks.
  const {
    values,
    names,
    errors,
    setErrors,
    setValues,
    onChange,
    onBlur,
    onFocus,
    inputHasError,
    validatorParams,
    setValidatorParams,
  } = useInputFields<ContourFormType>(
    contourInputInitialState,
    contourValidator,
    true,
    false,
    validatorInitialState,
  );

  // Api.
  const {
    data: accessTagsListResponse,
    isSuccess: isSuccessAccessTag,
    isLoading: isLoadingAccessTags,
  } = useAccessTagList();

  const {
    mutate: sendContourRequest,
    data: contourGenerationResponse,
    isSuccess: isSuccessContourGeneration,
    isError: isErrorContourGeneration,
    isPending: isLoadingContourGeneration,
    error: contourGenerationError,
    variables: contourPayload,
  } = useGenerateContour();

  const {
    data: workspaceFeaturesResponse,
    isFetching: isLoadingWorkspaceFeatures,
    isSuccess: isSuccessWorkspaceFeatures,
    isError: isErrorWorkspaceFeatures,
    error: workspaceFeaturesError,
    refetch: refetchWorkspaceFeatures,
  } = useFeatureList(
    values.workspaceLayer?.value.id ?? null,
    FeatureType.Polygon,
    false,
  );

  // Memoized values.
  const polygonLayerOptions: SelectOption<WorkspaceLayer>[] = useMemo(
    () =>
      getEligibleLayerOptions(
        activePolygonLayers,
        FeatureCountLabel.Polygons,
        1,
      ),
    [activePolygonLayers],
  );

  const visibleWorkspaceLayers: WorkspaceLayerListObj = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(workspaceLayers).filter(([, layer]) => layer.show),
      ),
    [workspaceLayers],
  );

  // useEffects.
  useEffect(() => {
    return () => {
      setVectorLayerVisibility(visibleWorkspaceLayers, true);
    };
  }, []);

  useEffect(() => {
    handleResponseErrorMessage(
      isErrorWorkspaceFeatures,
      workspaceFeaturesError,
    );
  }, [isErrorWorkspaceFeatures, workspaceFeaturesError]);

  useEffect(() => {
    if (isNil(values.workspaceLayer)) {
      return;
    }

    polygonTool?.deleteAllPolygons();
    refetchWorkspaceFeatures();
  }, [values.workspaceLayer]);

  useEffect(() => {
    if (
      !isSuccessWorkspaceFeatures ||
      isNil(workspaceFeaturesResponse?.data.features)
    ) {
      return;
    }

    loadPolygonFromWorkspace(workspaceFeaturesResponse.data.features);
  }, [isSuccessWorkspaceFeatures, workspaceFeaturesResponse]);

  useEffect(() => {
    if (isSuccessAccessTag && accessTagsListResponse) {
      const accessTags = accessTagsListResponse.data.list.map((accessTag) => {
        return {
          value: accessTag.id,
          label: accessTag.name,
        };
      });
      setAccessTagsList(accessTags);
    }
  }, [isSuccessAccessTag, accessTagsListResponse]);

  useEffect(() => {
    handleResponseMessage(
      isSuccessContourGeneration,
      isErrorContourGeneration,
      contourGenerationResponse,
      contourGenerationError,
    );

    trackEvent(Events.ContourGenerationCompleted, {
      [EventPropertyKey.AreaOfInterestType]: contourPayload?.polygonWkt
        ? MarkArea.Draw
        : contourPayload?.aoiLayerId
          ? MarkArea.LayerFromWorkspace
          : 'whole_dem',
      [EventPropertyKey.ProjectName]: selectedProject?.name,
      [EventPropertyKey.SiteName]: selectedTerrainSite?.name,
      [EventPropertyKey.IterationName]: selectedTerrainIteration?.name,
      [EventPropertyKey.Status]: isSuccessContourGeneration
        ? EventStatus.Success
        : EventStatus.Failure,
      [EventPropertyKey.FailureReason]: isErrorContourGeneration
        ? contourGenerationError?.meta.message
        : null,
    });

    if (isSuccessContourGeneration && contourGenerationResponse) {
      setVectorLayerVisibility(visibleWorkspaceLayers, true);
      polygonTool?.deleteAllPolygons();
      setShowContourForm(false);
    }
  }, [
    isSuccessContourGeneration,
    isErrorContourGeneration,
    contourGenerationError,
    contourGenerationResponse,
  ]);

  useEffect(() => {
    if (!dsmElevationBounds) {
      return;
    }

    setValidatorParams((prev) => ({
      ...prev,
      highestAltitude: {
        lowerBound: dsmElevationBounds.min,
        upperBound: dsmElevationBounds.max,
      },
      lowestAltitude: {
        lowerBound: dsmElevationBounds.min,
        upperBound: dsmElevationBounds.max,
      },
    }));
  }, [dsmElevationBounds]);

  useEffect(() => {
    setValidatorParams((prev) => ({
      ...prev,
      accessTags: {
        isRequired: layerAccessTagsRequired,
      },
    }));
  }, [layerAccessTagsRequired]);

  // Handlers.
  const isGenerateContourButtonDisabled = () => {
    if (
      currentMarkedArea === MarkArea.LayerFromWorkspace &&
      !isNil(values.workspaceLayer) &&
      isLoadingWorkspaceFeatures
    ) {
      return true;
    }

    if (layerAccessTagsRequired) {
      return inputHasError([names.workspaceLayer]);
    }
    return inputHasError([names.accessTags, names.workspaceLayer]);
  };

  const generateContour = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!dsmElevationBounds) {
      return;
    }

    if (!isGenerateContourButtonDisabled()) {
      const drawnPolygonsWkt = polygonTool?.exportCurrentPolygonsToWkt();
      const contourPayload: ContourPayloadType = {
        ...values,
        accessTags: values.accessTags?.map(
          (accessTagOption) => accessTagOption.value,
        ),
        iteration: selectedTerrainIteration?.id ?? '',
        ...(currentMarkedArea === MarkArea.Draw
          ? { polygonWkt: drawnPolygonsWkt?.[0]?.wkt ?? null }
          : { aoiLayerId: values.workspaceLayer?.value.id }),
      };

      sendContourRequest(contourPayload);
    }
  };

  const startDrawingHandler = () => {
    polygonTool?.deleteAllPolygons();
    polygonTool?.activate({ contourAoi: MarkArea.Draw }, {}, true);
    setIsDrawToolActive(true);
    dispatch(setCurrentActiveMapTool(MapTool.None));
  };

  const endDrawingHandler = () => {
    polygonTool?.deactivate();
    setIsDrawToolActive(false);
  };

  const resetDrawingHandler = () => {
    polygonTool?.deactivate();
    polygonTool?.deleteAllPolygons();
    polygonTool?.activate({ contourAoi: MarkArea.Draw }, {}, true);
  };

  const onToggleChange = (toggledLabel: string) => {
    const nextMarkedArea = snakeCase(toggledLabel) as MarkArea;

    if (nextMarkedArea === MarkArea.LayerFromWorkspace) {
      endDrawingHandler();
      setVectorLayerVisibility(workspaceLayers, false);
      setErrors((previousErrors) => ({
        ...previousErrors,
        workspaceLayer: isEmpty(polygonLayerOptions)
          ? FormErrorMessage.NoActivePolygonLayer
          : '',
      }));
    } else {
      setVectorLayerVisibility(visibleWorkspaceLayers, true);
      setErrors((previousErrors) => ({
        ...previousErrors,
        workspaceLayer: '',
      }));
      setValues((previousValues) => ({
        ...previousValues,
        workspaceLayer: null,
      }));
    }

    polygonTool?.deleteAllPolygons();
    setCurrentMarkedArea(nextMarkedArea);
  };

  const onWorkspaceLayerChange = (
    selectedLayer: SelectOption<WorkspaceLayer>,
  ) => {
    setValues((previousValues) => ({
      ...previousValues,
      workspaceLayer: selectedLayer,
    }));
  };

  const loadPolygonFromWorkspace = (features: FeatureResponseData[]) => {
    const polygon = features.find(
      (feature) => feature.type === FeatureType.Polygon,
    );

    if (isNil(polygon)) {
      return;
    }

    polygonTool?.importWKT(
      polygon.geometry,
      polygon.id,
      { contourAoi: MarkArea.LayerFromWorkspace },
      polygon.name,
    );
  };

  return isLoadingContourGeneration ? (
    <Spinner />
  ) : (
    <div className="generate-contour">
      <form onSubmit={generateContour} className="generate-contour__form">
        <MarkAreaToggle
          currentMarkedArea={currentMarkedArea}
          onToggleChange={onToggleChange}
          drawingHandlers={{
            startDrawingHandler,
            endDrawingHandler,
            resetDrawingHandler,
          }}
          headerComponent={
            <Input.Label className="mark-area__heading">
              Mark Area of Interest
            </Input.Label>
          }
          isDrawingToolEnabled={isDrawToolActive}
          workspaceLayerError={errors.workspaceLayer}
          selectLayerSelectProps={{
            options: polygonLayerOptions,
            value: values.workspaceLayer,
            name: names.workspaceLayer,
            isLoading: isLoadingWorkspaceFeatures,
            isDisabled: isEmpty(polygonLayerOptions),
            onChange: onWorkspaceLayerChange,
            onBlur,
            onFocus,
          }}
        />
        {/* Basic info for contour */}
        <InputGroup className="generate-contour__form__group">
          <Input.Label isRequired={true}>Contour Name</Input.Label>
          <Input.Text
            placeholder="Contour name"
            value={values.name}
            name={names.name}
            error={errors.name}
            {...{ onChange, onBlur, onFocus }}
          />
        </InputGroup>
        <InputGroup className="generate-contour__form__group">
          <Input.Label isRequired={layerAccessTagsRequired}>
            Access Tags
          </Input.Label>
          <Input.Select
            placeholder="Select Access Tags"
            options={accessTagsList}
            isMulti={true}
            name={names.accessTags}
            value={values.accessTags}
            error={errors.accessTags}
            isLoading={isLoadingAccessTags}
            onChange={(selectedAccessTags) => {
              setValues({ ...values, accessTags: selectedAccessTags });
            }}
            onBlur={() => {
              setErrors({
                ...errors,
                accessTags: contourValidator(
                  'accessTags',
                  values,
                  undefined,
                  validatorParams,
                ),
              });
            }}
            onFocus={() => {
              setErrors({
                ...errors,
                accessTags: '',
              });
            }}
          />
        </InputGroup>

        {/* Contour Interval */}
        <InputGroup className="generate-contour__form__group">
          <p className="generate-contour__form__header">Contour Interval</p>
          <div className="input-pair">
            <div className="input-pair__element">
              <Input.Label isRequired={true}>Minor Interval (m)</Input.Label>
              <Input.Number
                value={values.minorInterval}
                name={names.minorInterval}
                error={errors.minorInterval}
                min={0}
                onKeyDown={preventNonNumber}
                {...{ onChange, onBlur, onFocus }}
              />
            </div>
            <div className="input-pair__element">
              <Input.Label isRequired={true}>Major Interval (m)</Input.Label>
              <Input.Number
                value={values.majorInterval}
                name={names.majorInterval}
                error={errors.majorInterval}
                min={0}
                onKeyDown={preventNonNumber}
                {...{ onChange, onBlur, onFocus }}
              />
            </div>
          </div>
        </InputGroup>
        {/* Altitude Range */}
        <InputGroup className="generate-contour__form__group">
          <p className="generate-contour__form__header">Altitude Range</p>
          <div className="input-pair">
            <div className="input-pair__element">
              <Input.Label isRequired={true}>Lowest (m)</Input.Label>
              <Input.Number
                value={values.lowestAltitude}
                name={names.lowestAltitude}
                error={errors.lowestAltitude}
                onKeyDown={preventNonInteger}
                step={1}
                // lowest alitude (in contour) <= highest altitude (in dsm) to prevent selection of altitude values (in range highest altitude to ∞) which in-turn would generate no contour.
                max={dsmElevationBounds?.max}
                {...{ onChange, onBlur, onFocus }}
              />
            </div>
            <div className="input-pair__element">
              <Input.Label isRequired={true}>Highest (m)</Input.Label>
              <Input.Number
                value={values.highestAltitude}
                name={names.highestAltitude}
                error={errors.highestAltitude}
                onKeyDown={preventNonInteger}
                step={1}
                // highest altitude (in contour)>= lowest altitude (in dsm) to prevent selection of altitude values (in range 0 to lowest altitude) which in-turn would generate no contour.
                min={dsmElevationBounds?.min}
                {...{ onChange, onBlur, onFocus }}
              />
            </div>
          </div>
        </InputGroup>
        {/* Contour Smoothing  */}
        <InputGroup className="generate-contour__form__group">
          <p className="generate-contour__form__header">Contour Smoothing</p>
          <div className="input-pair">
            <div className="input-pair__element">
              <Input.Label isRequired={true}>Threshold Value (m)</Input.Label>
              <Input.Number
                value={values.thresholdValue}
                name={names.thresholdValue}
                error={errors.thresholdValue}
                min={0}
                onKeyDown={preventNonNumber}
                {...{ onChange, onBlur, onFocus }}
              />
            </div>
            <div className="input-pair__element">
              <Input.Label isRequired={true}>Smoothing Filter Size</Input.Label>
              <Input.Number
                value={values.smoothingFilterSize}
                name={names.smoothingFilterSize}
                error={errors.smoothingFilterSize}
                min={0}
                onKeyDown={preventNonNumber}
                {...{ onChange, onBlur, onFocus }}
              />
            </div>
          </div>
        </InputGroup>
        <Button
          className="generate-contour__btn"
          type="submit"
          disabled={isGenerateContourButtonDisabled()}
        >
          Generate Contour
        </Button>
      </form>
    </div>
  );
};
